import { format } from 'date-fns';
import { FC, useState } from 'react';
import { useDialog } from 'src/container/DialogContext';
import { useSessionCommentDelete, useSessionCommentUpdate } from 'src/generated/endpoint';
import { ResponseUserDto, SessionComment } from 'src/generated/model';
import { ButtonV2 } from '../common/ButtonV2';
import ConfirmDialog from '../common/ConfirmDialog';
import { TextareaV2 } from '../common/TextareaV2';
import { Typography } from '../common/Typography';
import SolidSVGIcon from '../icon/SolidSVGIcon';

interface ActivityCommentProps {
  comment: SessionComment;
  user: ResponseUserDto;
  refetch: () => void;
}

// 개별 댓글 컴포넌트
export const ActivityComment: FC<ActivityCommentProps> = ({ comment, user, refetch }) => {
  const { confirm } = useDialog();
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isUpdateMode, setUpdateMode] = useState(false);
  const [updatedContent, setUpdatedContent] = useState(comment.content || '');

  const { mutate: updateSessionComment, isLoading: updateSessionCommentLoading } = useSessionCommentUpdate({
    mutation: {
      onSuccess: () => {
        refetch();
        setUpdateMode(false);
      },
    },
  });

  const { mutate: deleteSessionComment, isLoading: deleteSessionCommentLoading } = useSessionCommentDelete({
    mutation: {
      onSuccess: () => {
        setDeleteModalOpen(false);
        refetch();
      },
      onError: () => {
        setDeleteModalOpen(false);
      },
    },
  });

  const handleUpdate = () => {
    if (!updatedContent.trim() || updateSessionCommentLoading) return;

    updateSessionComment({
      id: comment.id,
      data: { content: updatedContent },
    });
  };

  const date = comment.createdAt ? new Date(comment.createdAt) : new Date();
  const isMyComment = user.id === comment.userId;
  const commentContent = comment.content || '';

  if (isUpdateMode) {
    return (
      <div
        className={`flex flex-col gap-3 rounded-xl p-5 ${isMyComment ? 'bg-primary-gray-50' : 'bg-primary-orange-50'}`}
      >
        <div className="flex flex-row items-center justify-between">
          <span className="flex flex-row items-center gap-2">
            <SolidSVGIcon.Talk size={24} color={isMyComment ? 'gray700' : 'orange800'} weight="bold" />
            <Typography variant="title3" className={isMyComment ? '' : 'text-primary-orange-800'}>
              {isMyComment ? '내가 남긴 댓글' : '선생님의 피드백'}
            </Typography>
          </span>
          <Typography variant="caption" className="text-primary-gray-500">
            {format(date, 'yyyy.MM.dd HH:mm')}
          </Typography>
        </div>

        <TextareaV2
          value={updatedContent}
          onChange={(e) => setUpdatedContent(e.target.value)}
          className="h-[144px]"
          wrapperClassName="bg-white"
        />

        <div className="flex flex-row items-center justify-between">
          <ButtonV2 variant="outline" color="gray400" size={32} onClick={() => setUpdateMode(false)}>
            취소
          </ButtonV2>
          <ButtonV2
            variant="solid"
            color="orange100"
            size={32}
            disabled={!updatedContent.trim() || updateSessionCommentLoading}
            onClick={handleUpdate}
          >
            저장
          </ButtonV2>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl p-5 ${isMyComment ? 'bg-primary-gray-50' : 'bg-primary-orange-50'}`}
    >
      <div className="flex flex-row items-center justify-between">
        <span className="flex flex-row items-center gap-2">
          <SolidSVGIcon.Talk size={24} color={isMyComment ? 'gray700' : 'orange800'} weight="bold" />
          <Typography variant="title3" className={isMyComment ? '' : 'text-primary-orange-800'}>
            {isMyComment ? '내가 남긴 댓글' : '선생님의 피드백'}
          </Typography>
        </span>
        <Typography variant="caption" className="text-primary-gray-500">
          {format(date, 'yyyy.MM.dd HH:mm')}
        </Typography>
      </div>

      <div className="flex flex-col gap-1">
        <Typography variant="body2" className="whitespace-pre-line">
          {commentContent}
        </Typography>
      </div>

      {isMyComment && (
        <div className="flex flex-row items-center justify-start gap-2">
          <ButtonV2 variant="outline" color="gray400" size={32} onClick={() => setUpdateMode(true)}>
            수정
          </ButtonV2>
          <ButtonV2 variant="outline" color="gray400" size={32} onClick={() => setDeleteModalOpen(true)}>
            삭제
          </ButtonV2>
        </div>
      )}

      {isDeleteModalOpen && (
        <ConfirmDialog
          message="댓글을 삭제하시겠습니까?"
          description="삭제된 댓글은 복구할 수 없습니다."
          confirmText="삭제"
          cancelText="취소"
          onConfirm={() => deleteSessionComment({ id: comment.id })}
          onCancel={() => setDeleteModalOpen(false)}
          theme="primary"
        />
      )}
    </div>
  );
};
